'use client'

/**
 * Marketplace "Every platform Atrium speaks to" — collapsible accordion of
 * connectors grouped by channel. Each row shows the channel name + "N / M
 * connected" + chevron. Expanding the row reveals a grid of tokenized
 * connector cards (brand favicon + name + description + Connected pill,
 * Coming soon badge, or Enable CTA).
 *
 * `searchTerm` (passed from the marketplace search bar) hides rows that
 * don't match the connector name; matching channels auto-expand so the
 * hit is visible.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import type {
  CatalogChannelView,
  CatalogSubchannelView,
} from '@/lib/connectors/get-catalog'
import { sortSubchannelsForGrid } from '@/lib/connectors/sort-subchannels-for-grid'
import { applyVirtualSubchannels } from '@/lib/connectors/virtual-subchannels'
import { cn } from '@/lib/utils'
import { GuestAuthDialog } from '@/components/GuestAuthDialog'
import { SubchannelCard } from './SubchannelCard'

// Mirror lib/budget/channel-tokens-v3 since the marketplace uses the same
// per-channel palette but ChannelTokenV3 lives in /budget and importing
// from there for the marketplace surface is the wrong dependency direction.
const CHANNEL_META: Record<
  string,
  { label: string; dot: string; border: string; logo: string }
> = {
  search: {
    label: 'Search',
    dot: 'bg-ch-search',
    border: 'border-l-ch-search',
    logo: 'bg-[image:var(--tile-search)]',
  },
  social: {
    label: 'Social',
    dot: 'bg-ch-social',
    border: 'border-l-ch-social',
    logo: 'bg-[image:var(--tile-social)]',
  },
  tv: {
    label: 'TV / CTV',
    dot: 'bg-ch-tv',
    border: 'border-l-ch-tv',
    logo: 'bg-[image:var(--tile-tv)]',
  },
  programmatic: {
    label: 'Programmatic',
    dot: 'bg-ch-programmatic',
    border: 'border-l-ch-programmatic',
    logo: 'bg-[image:var(--tile-programmatic)]',
  },
  audio: {
    label: 'Audio',
    dot: 'bg-ch-audio',
    border: 'border-l-ch-audio',
    logo: 'bg-[image:var(--tile-audio)]',
  },
  retail: {
    label: 'Retail Media',
    dot: 'bg-ch-retail',
    border: 'border-l-ch-retail',
    logo: 'bg-[image:var(--tile-retail)]',
  },
}

function isLive(sub: CatalogSubchannelView): boolean {
  return (
    sub.connection?.status === 'connected' || sub.connection?.status === 'pending'
  )
}

export function ConnectorsAccordion({
  channels,
  searchTerm,
  initialOpenChannelKey,
  isLoggedIn = false,
  onOpen,
}: {
  channels: CatalogChannelView[]
  searchTerm: string
  initialOpenChannelKey?: string
  isLoggedIn?: boolean
  onOpen: (sub: CatalogSubchannelView) => void
}) {
  // Track which channels are open. Default: all collapsed.
  const [openChannels, setOpenChannels] = useState<Set<string>>(
    () => new Set(initialOpenChannelKey ? [initialOpenChannelKey] : []),
  )
  const [showGuestAuth, setShowGuestAuth] = useState(false)
  const channelRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const didScrollInitialRef = useRef(false)

  const term = searchTerm.trim().toLowerCase()
  const channelsWithVirtual = useMemo(() => applyVirtualSubchannels(channels), [channels])

  // Filter subchannels per channel by name. Channels with matches auto-expand.
  const filtered = useMemo(() => {
    return channelsWithVirtual.map((c) => {
      const visibleSubs = c.subchannels.filter((s) => s.status !== 'deprecated')
      if (!term) return { channel: c, subs: visibleSubs, matched: false }
      const subs = visibleSubs.filter(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          (s.description ?? '').toLowerCase().includes(term),
      )
      return { channel: c, subs, matched: subs.length > 0 }
    })
  }, [channelsWithVirtual, term])

  // When searching, hide channels with zero matches; auto-expand those with matches.
  const visible = term
    ? filtered.filter((f) => f.matched)
    : filtered

  const isOpen = (key: string) => (term ? true : openChannels.has(key))

  useEffect(() => {
    if (!initialOpenChannelKey || term) return
    setOpenChannels((prev) => {
      if (prev.has(initialOpenChannelKey)) return prev
      const next = new Set(prev)
      next.add(initialOpenChannelKey)
      return next
    })
  }, [initialOpenChannelKey, term])

  useEffect(() => {
    if (!initialOpenChannelKey || term || didScrollInitialRef.current) return
    if (!openChannels.has(initialOpenChannelKey)) return
    didScrollInitialRef.current = true
    window.setTimeout(() => {
      channelRefs.current[initialOpenChannelKey]?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      })
    }, 80)
  }, [initialOpenChannelKey, openChannels, term])

  const toggle = (key: string) => {
    if (term) return // search auto-expands; user can't collapse during search
    setOpenChannels((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleOpen = (sub: CatalogSubchannelView) => {
    if (!isLoggedIn) {
      setShowGuestAuth(true)
      return
    }
    onOpen(sub)
  }

  if (visible.length === 0) {
    return (
      <div className="px-1 py-6 font-mono text-[13px] text-text-muted">
        {term
          ? `No connectors match "${searchTerm}".`
          : 'Connector catalog loading. Sign in to see your workspace connections.'}
      </div>
    )
  }

  return (
    <div>
      <GuestAuthDialog
        open={showGuestAuth}
        onOpenChange={setShowGuestAuth}
        heading="Sign in to connect"
        body="Create a free account or sign in to connect your ad platforms and start running campaigns."
      />
      {visible.map(({ channel, subs }) => {
        const meta = CHANNEL_META[channel.key]
        if (!meta) return null
        const live = subs.filter(isLive).length
        const total = subs.length
        const open = isOpen(channel.key)
        return (
          <div
            key={channel.key}
            ref={(node) => {
              channelRefs.current[channel.key] = node
            }}
            className="mb-2 scroll-mt-6"
          >
            <button
              type="button"
              onClick={() => toggle(channel.key)}
              className={cn(
                'flex w-full items-center gap-3 rounded-[12px] border-[0.5px] border-line px-4 py-3 text-left transition-colors hover:bg-canvas',
                open ? 'bg-canvas' : 'bg-surface',
              )}
            >
              <span aria-hidden className={cn('size-2.5 flex-none rounded-full', meta.dot)} />
              <span className="text-sm font-bold">{meta.label}</span>
              <span className="font-mono text-xs text-text-muted-2">
                {live} / {total} connected
              </span>
              <HugeiconsIcon
                icon={open ? ArrowUp01Icon : ArrowDown01Icon}
                className="ml-auto size-4 text-[#A8A69C]"
              />
            </button>

            {open && subs.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(244px,1fr))] gap-3 px-1 py-3">
                {sortSubchannelsForGrid(subs).map((sub) => (
                  <SubchannelCard
                    key={sub.key}
                    sub={sub}
                    channelKey={channel.key}
                    onOpen={handleOpen}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
